import { ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, Input, Output, EventEmitter, SimpleChanges, inject } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormControl, UntypedFormArray, AbstractControl } from '@angular/forms';
import * as _ from 'lodash-es';
import { DateFormatValidator } from '../../directives/date-format-validator.directive';
import { debounceTime } from 'rxjs/operators';
import { AccountContextService } from '../../services/account-context.service';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { SettingsService } from 'src/app/modules/account/settings/account-settings.service';
import { numberValidator } from '../../validators/number.validator';
import { emailValidator } from '../../validators/email.validator';
import { urlValidator } from '../../validators/url.validator';
import { lastValueFrom, Subscription } from 'rxjs';
import { stripeDomainValidator } from '../../validators/strip-domain.validator';
import { comparisonInputType, comparisonValueType, predicateGroupName, Rule, RuleField, RuleOperatorConfig, ruleOperatorType, RuleOptionsConfig, RulesConfig, RulesGroup, RulesOptions } from '../../models/rules.models';
import { predicateGroups } from '../../constants/rule.constants';
import { RulesService } from 'src/app/modules/account/services/rules.service';

export interface RuleSavedEvent {
  isValid: boolean;
  rulesObject: RulesConfig;
}

@Component({
  selector: 'app-rule-engine-generator',
  templateUrl: './rule-engine-generator.component.html',
  styleUrls: ['./rule-engine-generator.component.scss'],
  standalone: false
})
export class RuleEngineGeneratorComponent implements OnInit, OnChanges, OnDestroy {
  private accountContext = inject(AccountContextService);
  private settingsService = inject(SettingsService);
  private rulesService = inject(RulesService);
  private cdr = inject(ChangeDetectorRef);

  @Input() rulesOptions: RulesOptions;
  @Input() rulesData: RulesConfig;
  @Input() ruleFields: RuleField[];
  @Input() autocompleteFieldName = false;
  @Input() hideLoader = false;
  @Input() disableFieldValidation = false;
  usedRuleFields: RuleField[];

  config: RuleOptionsConfig = {
    singleGroup: false,
    fixedRules: false,
    fixedPredicate: false,
    rulesRequired: false,
    hideGroupsOperatorToggle: false
  };

  daysPredicateOptions = [
    { name: 7, label: 'in the last 7 days' },
    { name: 30, label: 'in the last 30 days' },
    { name: 90, label: 'in the last 90 days' }
  ];

  sfDaysPredicateOptions = [
    { name: 90, label: 'and updated within the past 90 days' },
    { name: 365, label: 'and updated within the past year' },
    { name: 0, label: 'and updated at any time' }
  ];

  rules!: UntypedFormGroup;
  comparisonInputType = comparisonInputType;
  comparisonValueType = comparisonValueType;
  ruleFieldGrouped!: { group: any; fields: RuleField[]; hidden: boolean }[];
  predicateGroupsDef = predicateGroups;
  @Output() saved = new EventEmitter<RuleSavedEvent>();
  predicateGroupName = predicateGroupName;
  readonly chipsSeparatorKeysCodes: number[] = [ENTER, COMMA];
  private rulesValueChangesSubscription?: Subscription;
  private isInitialized = false;
  private lastIncomingComparableRules: RulesConfig | null = null;

  async ngOnInit() {
    // filter rules by feature
    await this.accountContext.waitForInit();
    this.usedRuleFields = this.ruleFields.filter(r => !r.feature || this.accountContext.supports(r.feature));

    // filter rules by client app
    const accountFeatures = await lastValueFrom(this.settingsService.getSettingsAccountFeatures());
    const clientApps = accountFeatures.clientApps ?? [];
    this.usedRuleFields = this.usedRuleFields.filter(r => !r.clientApp || clientApps.includes(r.clientApp));
    this.groupRulesFields(this.usedRuleFields);

    this.rules = new UntypedFormGroup({
      rulesOperator: new UntypedFormControl(this.rulesData?.rulesOperator || 'Or'),
      rulesGroups: new UntypedFormArray([])
    });

    if (this.rulesData) {
      this.loadData(this.rulesData);
    } else {
      this.addRulesGroup();
    }

    if (this.rulesOptions?.config) {
      this.config = { ...this.config, ...this.rulesOptions.config };
    }

    this.buildRulesAndSend(this.rules);

    this.rules.valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.buildRulesAndSend(this.rules);
      });

    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.rules || !changes.rulesData) {
      return;
    }

    const incomingRules = this.buildComparableRulesConfig(this.rulesData);
    if (_.isEqual(incomingRules, this.lastIncomingComparableRules)) {
      return;
    }

    const currentRules = this.getComparableRulesConfigFromForm();
    if (_.isEqual(incomingRules, currentRules)) {
      this.lastIncomingComparableRules = incomingRules ? _.cloneDeep(incomingRules) : null;
      return;
    }

    this.rebuildFormFromRulesData(this.rulesData);
    this.lastIncomingComparableRules = incomingRules ? _.cloneDeep(incomingRules) : null;
  }

  ngOnDestroy(): void {
    this.rulesValueChangesSubscription?.unsubscribe();
  }

  loadData(data: RulesConfig = this.rulesData) {
    if (!data?.rulesGroups?.length) {
      return;
    }

    const loadData = _.cloneDeep(data);
    let index = 0;


    loadData.rulesGroups.forEach(group => {
      this.addRulesGroup(group);
      group.rules.forEach(line => {
        if (line.comparisonPredicate) {
          if (line.comparisonPredicate.includes('Days')) {
            line.daysPredicate = +line.comparisonPredicate.replace(/[a-zA-Z]/g, '');
            line.comparisonPredicate = line.comparisonPredicate.replace(/[0-9]/, '{days}').replace(/[0-9]/g, '');
          }
          if (this.isArrayInputType(this.getInputType(line.comparisonPredicate))) {
            line.comparisonArray = line.comparisonValue.split('|').map(v => this.decodeArrayValue(v));
          }
          this.addRuleLine(index, line);
        } else {
          this.addRuleLine(index);
        }
      });
      index++;
    });
  }

  initRulesGroup(group?: RulesGroup) {
    return new UntypedFormGroup({
      rulesOperator: new UntypedFormControl(group?.rulesOperator || 'Or'),
      rules: new UntypedFormArray([])
    });
  }
  initRuleLine(rule?: Rule) {
    const line = new UntypedFormGroup({
      comparisonPredicate: new UntypedFormControl(rule?.comparisonPredicate),
      comparisonOperator: new UntypedFormControl(rule?.comparisonOperator),
      comparisonValue: new UntypedFormControl(rule?.comparisonValue || ''),
      comparisonArray: new UntypedFormControl(rule?.comparisonArray || []),
      daysPredicate: new UntypedFormControl(rule?.daysPredicate || 90)
    });

    if (rule) {
      this.setValidators(line);
      this.setValidatorsBasedOnOperator(line, rule.comparisonOperator);
    }

    return line;
  }

  addRuleLine(j, rule?: Rule) {
    const group = this.rules.get('rulesGroups') as UntypedFormArray;
    const control = group.controls[j].get('rules') as UntypedFormArray;
    const fixedField = this.config.fixedPredicate && this.usedRuleFields?.length === 1
      ? this.usedRuleFields[0]
      : null;
    const defaultRule = fixedField && !rule ? {
      comparisonPredicate: fixedField.predicate,
      comparisonOperator: this.rulesService.getOperators(fixedField.operatorType)?.[0]?.name ?? '',
      comparisonValue: ''
    } : rule;
    const newLine = this.initRuleLine(defaultRule);
    control.push(newLine);
    return false;
  }

  addRulesGroup(group?: RulesGroup) {
    const control = this.rules.get('rulesGroups') as UntypedFormArray;
    control.push(this.initRulesGroup(group));
    if (!group) {
      this.addRuleLine(control.length - 1);
    }
    return false;
  }

  removeRuleLine(groupIndex: number, lineIndex: number) {
    const group = this.rules.get('rulesGroups') as UntypedFormArray;
    const control = group.controls[groupIndex].get('rules') as UntypedFormArray;
    control.removeAt(lineIndex);
  }

  getGroupOperator(groupIndex: number): string {
    const group = this.rules.get('rulesGroups') as UntypedFormArray;
    return group.controls[groupIndex].get('rulesOperator').value;
  }

  getGeneralOperator(): string {
    return this.rules.get('rulesOperator').value;
  }

  removeRulesGroup(index: number) {
    const control = this.rules.get('rulesGroups') as UntypedFormArray;
    control.removeAt(index);
    return false;
  }
  getRulesGroups(form: UntypedFormGroup) {
    return (form.controls.rulesGroups as UntypedFormArray).controls;
  }
  getRuleLines(form: UntypedFormGroup) {
    return (form.controls.rules as UntypedFormGroup).controls;
  }
  resetLineDataOnChange(value, line) {
    const options = this.getOptions(value);
    line.patchValue({
      comparisonOperator: options[0].name,
      comparisonValue: '',
      comparisonArray: []
    });
    this.setValidatorsByInputType(line, this.getInputType(value));
    this.setValidatorsBasedOnOperator(line, options[0].name);
  }

  onPredicateChange(line: UntypedFormGroup): void {
    line.patchValue({
      comparisonOperator: null,
      comparisonValue: '',
      comparisonArray: []
    });
    this.setValidators(line);
    line.get('comparisonOperator').markAsUntouched();
    line.get('comparisonOperator').markAsPristine();
    line.get('comparisonValue').markAsUntouched();
    line.get('comparisonValue').markAsPristine();
    line.get('comparisonArray').markAsUntouched();
    line.get('comparisonArray').markAsPristine();
  }

  onOperatorChange(value, line: UntypedFormGroup) {
    this.setValidators(line);
    this.setValidatorsBasedOnOperator(line, value);
  }

  private setValidators(line: UntypedFormGroup): void {
    if (this.disableFieldValidation) {
      ['comparisonOperator', 'comparisonValue', 'comparisonArray'].forEach(key => {
        const ctrl = line.get(key);
        ctrl?.clearValidators();
        ctrl?.setErrors(null);
        ctrl?.updateValueAndValidity({ emitEvent: false });
      });
      return;
    }

    let inputType = this.getInputType(line.get('comparisonPredicate').value);

    // if (line.get('comparisonOperator').value === 'CollectionElementContains') {
    //   inputType = comparisonInputType.textField;
    //   line.patchValue({
    //     comparisonValue: '',
    //     comparisonArray: []
    //   });
    // }
    this.setValidatorsByInputType(line, inputType);
  }

  private setValidatorsByInputType(line: any, inputType: comparisonInputType) {
    if (this.disableFieldValidation) {
      return;
    }

    line.get('comparisonOperator').setValidators([Validators.required]);
    line.get('comparisonOperator')?.updateValueAndValidity();

    switch (inputType) {
      case comparisonInputType.number:
        line.get('comparisonValue').setValidators([Validators.required, numberValidator]);
        line.get('comparisonArray').clearValidators();
        break;
      case comparisonInputType.dateTime:
        line.get('comparisonValue').setValidators([Validators.required, DateFormatValidator()]);
        line.get('comparisonArray').clearValidators();
        break;
      case comparisonInputType.multiselect:
      case comparisonInputType.autocomplete:
      case comparisonInputType.chips:
      case comparisonInputType.freeTextAutocomplete:
        line.get('comparisonArray').setValidators([Validators.required]);
        line.get('comparisonValue').clearValidators();
        break;
      case comparisonInputType.hidden:
        line.get('comparisonArray').clearValidators();
        line.get('comparisonValue').clearValidators();
        break;
      default:
        line.get('comparisonValue').setValidators([Validators.required]);
        line.get('comparisonArray').clearValidators();
        break;
    }
    line.get('comparisonValue').updateValueAndValidity();
    line.get('comparisonArray').updateValueAndValidity();
  }

  private setValidatorsBasedOnOperator(line: any, comparisonOperatorName: string) {
    const inputType = this.getInputType(line.get('comparisonPredicate').value);
    if (comparisonOperatorName === 'StringNullOrEmpty' || comparisonOperatorName === 'StringNotNullOrEmpty') {
      line.get('comparisonValue').clearValidators();
    }
    if (comparisonOperatorName === 'StringEqualsCaseInsensitive' || comparisonOperatorName === 'StringNotEqualsCaseInsensitive') {
      if (inputType === comparisonInputType.email) {
        line.get('comparisonValue').setValidators([Validators.required, emailValidator]);
      }
      if (inputType === comparisonInputType.url) {
        line.get('comparisonValue').setValidators([Validators.required, urlValidator, stripeDomainValidator]);
      }
    } else {
      if (inputType === comparisonInputType.email) {
        line.get('comparisonValue').setValidators([Validators.required]);
      }
      if (inputType === comparisonInputType.url) {
        line.get('comparisonValue').setValidators([Validators.required, stripeDomainValidator]);
      }
    }
    line.get('comparisonValue').updateValueAndValidity();
  }

  getOptions(value: string): RuleOperatorConfig[] {
    const el = this.usedRuleFields.find(element => element.predicate === value);
    let operatorType = ruleOperatorType.ruleCollectionOperators;
    if (el) {
      operatorType = el.operatorType;
    }
    return this.rulesService.getOperators(operatorType) ?? this.rulesService.getOperators(ruleOperatorType.ruleCollectionOperators);
  }

  getPlaceholder(value): string {
    return this.usedRuleFields.find(e => e.predicate === value)?.valuePlaceholder || 'Choose Value';
  }

  ifDaysPredicate(value): any {
    if (value) {
      const el = this.usedRuleFields.find(element => element.predicate === value);
      return el.isDaysPredicate;
    }
    return false;
  }

  ifSalesforceField(value): any {
    if (value) {
      const el = this.usedRuleFields.find(element => element.predicate === value);
      if (el.group === predicateGroupName.salesforce) {
        return true;
      }
    }
    return false;
  }

  buildRulesAndSend(form: UntypedFormGroup) {
    const viewModelRuleConfig: RulesConfig = form.getRawValue();
    if (viewModelRuleConfig.rulesGroups.length === 1
      && viewModelRuleConfig.rulesGroups[0].rules.length === 1
      && viewModelRuleConfig.rulesGroups[0].rules[0].comparisonPredicate === null) {
      return null;
    }

    viewModelRuleConfig.rulesGroups.forEach(ruleGroup => {
      ruleGroup.rules = ruleGroup.rules.filter(r => r.comparisonOperator?.length || r.comparisonPredicate?.length);
    });

    const validRuleGroups = viewModelRuleConfig.rulesGroups.filter(gr => gr.rules?.length);

    if (validRuleGroups.length === 0) { return null; }

    const validObj: RulesConfig = {
      rulesOperator: viewModelRuleConfig.rulesOperator,
      rulesGroups: validRuleGroups
    } as RulesConfig;

    this.disableEnableFieldsGroup(predicateGroupName.salesforce, false);

    validObj.rulesGroups.forEach((group) => {
      group.rules.forEach(line => {
        if (line.comparisonPredicate === 'PageContext.Url' || line.comparisonPredicate === 'PageContext.Path') {
          line.comparisonValue = line.comparisonValue.trim();
        }
        let inputType = this.getInputType(line.comparisonPredicate);
        if (line.comparisonOperator === 'CollectionElementContains') {
          inputType = comparisonInputType.textField;
        }
        if (this.isArrayInputType(inputType)) {
          line.comparisonValue = line.comparisonArray.map(v => this.encodeArrayValue(v)).join('|');
        }
        if (line.comparisonPredicate) {
          // we can configure SF field only once per Audience
          if (line.comparisonPredicate.includes('CrmDealStages')) {
            this.disableEnableFieldsGroup(predicateGroupName.salesforce, true);
          }
          if (line.comparisonPredicate.includes('{days}')) {
            line.comparisonPredicate = line.comparisonPredicate.replace('{days}', line.daysPredicate.toString());
          }
          switch (this.getInputType(line.comparisonPredicate)) {
            case comparisonInputType.number: {
              line.predicateType = 'Double';
              break;
            }
            case comparisonInputType.dateTime: {
              line.predicateType = 'DateTime';
              break;
            }
          }
        }
        delete line.daysPredicate;
        delete line.comparisonArray;
      });
    });
    this.saved.emit({ isValid: form.valid, rulesObject: validObj });
  }

  getInputType(value): comparisonInputType {
    const el = this.usedRuleFields.find(element => element.predicate === value);
    let inputType = comparisonInputType.number;
    if (el && el.fieldType !== undefined) {
      inputType = el.fieldType;
    }
    return inputType;
  }

  getPredicateName(predicate: string) {
    return this.usedRuleFields.find(f => f.predicate === predicate)?.name;
  }

  getPredicateValuesGroupedOrNot(predicate: string) {
    return this.usedRuleFields.find(f => f.predicate === predicate)?.valuesGrouped;
  }

  getCollection(value: string): any {
    const field = this.usedRuleFields.find(f => f.predicate === value);
    return field.availableValues ?? this.rulesOptions.data[field.valueType];
  }

  getCollectionValues(value): any {
    return this.getCollection(value).map(v => typeof v === 'object' ? v.name : v);
  }

  printProperty(object, prop): string {
    if (typeof object === 'string' || object instanceof String) {
      return object.toString();
    }
    return object[prop].toString();
  }

  groupRulesFields(usedRuleFields: RuleField[]) {
    const groupToValues = usedRuleFields.reduce((obj, item) => {
      let isValid = true;
      if ((this.getCollection(item.predicate) === undefined || this.getCollection(item.predicate).length === 0)
        && (item.fieldType === comparisonInputType.multiselect || item.fieldType === comparisonInputType.autocomplete)) {
        isValid = false;
      }
      if (isValid) {
        obj[item.group] = obj[item.group] || [];
        obj[item.group].push(item);
      }
      return obj;
    }, {});

    this.ruleFieldGrouped = Object.keys(groupToValues).map((key) =>
      ({ group: key, fields: groupToValues[key], hidden: false })
    );
  }

  predicateGroupNameByValue(name: string): string {
    const group = this.predicateGroupsDef.find(e => +e.name === +name);
    if (group.showTitle) {
      return this.predicateGroupsDef.find(e => +e.name === +name).label;
    }
    return '';
  }

  resetAllRules() {
    this.rules = new UntypedFormGroup({
      rulesOperator: new UntypedFormControl(this.rulesData?.rulesOperator || 'Or'),
      rulesGroups: new UntypedFormArray([])
    });
    this.addRulesGroup();
    this.saved.emit({ isValid: !this.config.rulesRequired, rulesObject: null });
    this.subscribeToRuleChanges();
    return false;
  }

  disableEnableFieldsGroup(predicate: predicateGroupName, disable: boolean) {
    const group = this.ruleFieldGrouped.filter(g => g.group === predicate.toString());
    if (group.length > 0) {
      group[0].hidden = disable;
    }
  }

  addToValuesArray(field: AbstractControl, event: MatChipInputEvent): void {
    const input = event.chipInput;
    const value = event.value;

    if ((value || '').trim()) {
      field.value.push(value.trim());
    }
    if (input) {
      input.inputElement.value = '';
    }
    field.updateValueAndValidity();
  }

  removeFromValuesArray(field: AbstractControl, value: string): void {
    const index = field.value.indexOf(value);
    if (index >= 0) {
      field.value.splice(index, 1);
    }
    field.updateValueAndValidity();
    field.markAsDirty();
  }

  private isArrayInputType(inputType: comparisonInputType): boolean {
    return inputType === comparisonInputType.multiselect
      || inputType === comparisonInputType.autocomplete
      || inputType === comparisonInputType.chips
      || inputType === comparisonInputType.freeTextAutocomplete;
  }

  private encodeArrayValue(value: string): string {
    return value.replaceAll('|', '%7C');
  }

  private decodeArrayValue(value: string): string {
    return value.replaceAll('%7C', '|');
  }

  private subscribeToRuleChanges(): void {
    this.rulesValueChangesSubscription?.unsubscribe();
    this.rulesValueChangesSubscription = this.rules.valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => this.buildRulesAndSend(this.rules));
  }

  private rebuildFormFromRulesData(rulesData: RulesConfig | null | undefined): void {
    this.rules.get('rulesOperator')?.setValue(rulesData?.rulesOperator || 'Or', { emitEvent: false });
    this.clearRulesGroups();
    if (rulesData?.rulesGroups?.length) {
      this.loadData(rulesData);
    } else {
      this.addRulesGroup();
    }
    this.buildRulesAndSend(this.rules);
  }

  private clearRulesGroups(): void {
    const groups = this.rules.get('rulesGroups') as UntypedFormArray;
    while (groups.length > 0) {
      groups.removeAt(0);
    }
  }

  private getComparableRulesConfigFromForm(): RulesConfig | null {
    return this.buildComparableRulesConfig(this.rules.getRawValue());
  }

  private buildComparableRulesConfig(rulesConfig: RulesConfig | null | undefined): RulesConfig | null {
    if (!rulesConfig?.rulesGroups?.length) {
      return null;
    }

    const comparableRules = _.cloneDeep(rulesConfig);
    comparableRules.rulesGroups.forEach(group => {
      group.rules = (group.rules ?? []).filter(rule => rule.comparisonOperator?.length || rule.comparisonPredicate?.length);
      group.rules.forEach(line => {
        if (line.comparisonPredicate === 'PageContext.Url' || line.comparisonPredicate === 'PageContext.Path') {
          line.comparisonValue = line.comparisonValue?.trim();
        }
        if (line.comparisonPredicate?.includes('{days}')) {
          line.comparisonPredicate = line.comparisonPredicate.replace('{days}', line.daysPredicate?.toString() ?? '');
        }
        let inputType = this.getInputType(line.comparisonPredicate);
        if (line.comparisonOperator === 'CollectionElementContains') {
          inputType = comparisonInputType.textField;
        }
        if (this.isArrayInputType(inputType)) {
          const valuesFromArray = line.comparisonArray ?? [];
          const valuesFromString = (line.comparisonValue ?? '')
            .split('|')
            .filter(value => value.length > 0)
            .map(value => this.decodeArrayValue(value));
          const values = valuesFromArray.length > 0 ? valuesFromArray : valuesFromString;
          line.comparisonValue = values.map(value => this.encodeArrayValue(value)).join('|');
        }
        delete line.daysPredicate;
        delete line.comparisonArray;
        delete line.predicateType;
      });
    });

    comparableRules.rulesGroups = comparableRules.rulesGroups.filter(group => (group.rules?.length ?? 0) > 0);
    if (comparableRules.rulesGroups.length === 0) {
      return null;
    }

    return {
      rulesOperator: comparableRules.rulesOperator,
      rulesGroups: comparableRules.rulesGroups
    };
  }
}
